import React, { useEffect, useState, useRef, useCallback } from 'react';
import { 
  Container, Grid, Box, Typography, List, ListItem, Divider, Chip, IconButton, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Paper
} from '@mui/material';
import { styled } from '@mui/system';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { ChevronLeft, ChevronRight, Print, Download, DragIndicator, Reorder } from '@mui/icons-material';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Custom styled components for consistent design
const SectionHeader = styled(Typography)(({ theme, color }) => ({
  fontFamily: 'serif',
  fontWeight: 'bold',
  color: color || '#2c3e50',
  textTransform: 'uppercase',
  position: 'relative',
  paddingBottom: '8px',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: '50px',
    height: '3px',
    backgroundColor: color || '#3498db',
    borderRadius: '5px',
  },
}));

const ContactInfo = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '4px',
  '& .MuiSvgIcon-root': {
    fontSize: '1rem',
    marginRight: '8px',
    color: color || '#3498db',
  },
}));

const BulletPoint = styled('span')(({ color }) => ({
  marginRight: '8px',
  color: color || '#34495e',
}));

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultData = {
  firstName: "John",
  lastName: "Doe",
  currentPosition: "Software Engineer",
  phone: "+91 98765 43210",
  email: "john.doe@email.com",
  city: "Chennai, India",
  linkedIn: "",
  profile: "Experienced software engineer with a strong background in full-stack development, cloud solutions, and problem solving.",
  skills: [
    "React, Node.js, MongoDB",
    "Java, Spring Boot",
    "AWS, Docker",
    "Problem Solving",
  ],
};

const defaultSectionOrder = [
  'about',
  'skills',
  'languages',
  'certifications',
  'softwareSkills',
  'interests',
  'accomplishments',
  'experience',
  'education',
  'additionalInfo',
  'volunteering',
  'references',
  'websites'
];

const Resume18 = ({
  color = '#2c3e50',
  nameColor = '#2c3e50',
  sidebarBackground = '#f8f9fa',
  headerBackground = '#fff',
  font = 'Arial, sans-serif',
  fontSize = '14px',
  fontStyle = 'normal',
  headingSize = '24px',
  sectionSpacing = 30,
  paragraphSpacing = 10,
  lineSpacing = 20,
  topBottomMargin = 30,
  sideMargins = 20,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = 'A4',
  theme,
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
  formData = {},
}) => {
  // ✅ FIX: primaryColor only for HEADINGS, everything else uses fixed dark colors
  const primaryColor = color; // Only used for section headings

  // ✅ FIX: Separate palette - headingColor changes, textColor stays dark always
  const palette = {
    mainBackground: '#FFFFFF',
    sidebarBackground: sidebarBackground,
    headingColor: primaryColor,   // ✅ Only headings get the selected color
    textColor: '#333333',          // ✅ Fixed dark color for all body text
    lightText: '#555555',          // ✅ Fixed gray for secondary text
    borderColor: '#cccccc',        // ✅ Fixed gray for borders/dividers
    header: headerBackground,
    nameColor: nameColor,
    iconColor: '#555555',          // ✅ Fixed color for icons
  };

  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const resumeRef = useRef(null);

  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});
  
  const data = { ...defaultData, ...formData };

  const [sectionOrder, setSectionOrder] = useState(defaultSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) {
      try {
        setEducationEntries(JSON.parse(storedEntries));
      } catch (error) {
        setEducationEntries([]);
      }
    }
    
    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) setSavedSkills(JSON.parse(storedSkills));
    
    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);
    
    const storedAdditionalInfo = JSON.parse(localStorage.getItem("additionalInfo")) || [];
    setSavedAdditionalInfo(storedAdditionalInfo);
    
    const storedLanguages = JSON.parse(localStorage.getItem("languagesList")) || [];
    setSavedLanguages(storedLanguages);
    
    const storedAccomplishments = JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
    setSavedAccomplishments(storedAccomplishments);
    
    const storedCertifications = JSON.parse(localStorage.getItem("certificationsList")) || [];
    setSavedCertifications(storedCertifications);
    
    const storedReferences = JSON.parse(localStorage.getItem("referencesList")) || [];
    setSavedReferences(storedReferences);
    
    const storedSoftwareSkills = JSON.parse(localStorage.getItem("softwareSkills")) || [];
    setSoftwareSkills(storedSoftwareSkills);
    
    const storedVolunteering = JSON.parse(localStorage.getItem("volunteering")) || [];
    setSavedVolunteering(storedVolunteering);
    
    const storedInterests = JSON.parse(localStorage.getItem("interests")) || [];
    setSavedInterests(storedInterests);
    
    const storedWebsites = JSON.parse(localStorage.getItem("websites")) || [];
    setSavedWebsites(storedWebsites);
    
    const userId = localStorage.getItem("userId");
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      if (currentCustomSections.length > 0) {
        currentCustomSections.forEach(sectionName => {
          const userCustomKey = `custom_${userId}_${sectionName}`;
          try {
            const customSectionData = localStorage.getItem(userCustomKey);
            if (customSectionData) {
              const parsedData = JSON.parse(customSectionData);
              const items = parsedData.items || [];
              const validItems = items.filter(item => item && item.trim() !== '');
              if (validItems.length > 0) {
                sectionsData[sectionName] = validItems;
              }
            }
          } catch (error) {
            console.error(`Error loading custom section ${sectionName}:`, error);
          }
        });
      }
    }

    setCustomSections(sectionsData);

    const savedSectionOrder = localStorage.getItem("resume18SectionOrder");
    if (savedSectionOrder) {
      const parsedOrder = JSON.parse(savedSectionOrder);
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      const filteredOrder = parsedOrder.filter(section => !section.startsWith('custom_'));
      const allSections = [...filteredOrder, ...currentCustomSectionIds];
      setSectionOrder(allSections);
    } else {
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      const updatedSectionOrder = [...defaultSectionOrder, ...currentCustomSectionIds];
      setSectionOrder(updatedSectionOrder);
    }
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId && Object.keys(customSections).length > 0) {
      const currentOrder = [...sectionOrder];
      const currentCustomSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
      const filteredOrder = currentOrder.filter(section => !section.startsWith('custom_'));
      const newOrder = [...filteredOrder, ...currentCustomSectionIds];
      if (JSON.stringify(newOrder) !== JSON.stringify(currentOrder)) {
        setSectionOrder(newOrder);
        localStorage.setItem("resume18SectionOrder", JSON.stringify(newOrder));
      }
    } else if (userId && Object.keys(customSections).length === 0) {
      const currentOrder = [...sectionOrder];
      const filteredOrder = currentOrder.filter(section => !section.startsWith('custom_'));
      if (filteredOrder.length !== currentOrder.length) {
        setSectionOrder(filteredOrder);
        localStorage.setItem("resume18SectionOrder", JSON.stringify(filteredOrder));
      }
    }
  }, [customSections]);

  useEffect(() => {
    if (sectionOrder.length > 0) {
      localStorage.setItem("resume18SectionOrder", JSON.stringify(sectionOrder));
    }
  }, [sectionOrder]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, sectionId: sectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    if (sourceIndex !== targetIndex) {
      const newSectionOrder = [...sectionOrder];
      const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
      newSectionOrder.splice(targetIndex, 0, movedSection);
      setSectionOrder(newSectionOrder);
    }
    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetSectionOrder = () => setSectionOrder(defaultSectionOrder);
  const handleArrangeSectionsClick = () => setArrangeDialogOpen(true);
  const handleArrangeDialogClose = () => setArrangeDialogOpen(false);

  const getSectionTitle = (sectionId) => {
    const titles = {
      'about': 'About Me', 'skills': 'Skills', 'experience': 'Work Experience',
      'education': 'Education', 'languages': 'Languages', 'certifications': 'Certifications',
      'accomplishments': 'Accomplishments', 'softwareSkills': 'Software Skills',
      'volunteering': 'Volunteering', 'interests': 'Interests',
      'websites': 'Websites/Profiles', 'references': 'References',
      'additionalInfo': 'Additional Information'
    };
    if (sectionId.startsWith('custom_')) {
      const customName = sectionId.replace('custom_', '').replace(/_/g, ' ');
      return customName.charAt(0).toUpperCase() + customName.slice(1);
    }
    return titles[sectionId] || sectionId;
  };

  const renderDragPanel = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <DragIndicator sx={{ mr: 1 }} />
        Resume Sections
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
          ({sectionOrder.length} sections)
        </Typography>
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sectionOrder.map((sectionId, index) => (
          <Paper
            key={sectionId}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            sx={{
              p: 2, mb: 1, display: 'flex', alignItems: 'center',
              backgroundColor: dragOverIndex === index ? '#f0f0f0' : 'white',
              boxShadow: dragOverIndex === index ? 2 : 1,
              cursor: 'grab', '&:active': { cursor: 'grabbing' }, transition: 'all 0.2s ease',
            }}
          >
            <DragIndicator color="action" />
            <Typography sx={{ ml: 1, flexGrow: 1 }}>{getSectionTitle(sectionId)}</Typography>
            <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
          </Paper>
        ))}
      </Box>
      <Button onClick={resetSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
        Reset Section Order
      </Button>
    </Box>
  );

  const renderSectionContent = (sectionId) => {
    // ✅ FIX: Section container - uses fixed textColor, NOT headingColor
    const sectionContainerStyle = {
      mb: `${sectionSpacing / 10}px`,
      fontFamily: font,
      fontSize: fontSize,
      fontStyle: fontStyle,
      lineHeight: `${lineSpacing}px`,
      color: palette.textColor,  // ✅ Fixed dark color
    };

    // ✅ FIX: Paragraph style - fixed dark color
    const paragraphStyle = {
      mb: `${paragraphSpacing / 10}px`,
      fontFamily: font,
      fontSize: fontSize,
      fontStyle: fontStyle,
      lineHeight: `${lineSpacing}px`,
      color: palette.textColor,  // ✅ Fixed dark color
      textIndent: `${paragraphIndent}px`
    };

    // ✅ FIX: Heading style - uses headingColor (the selected color)
    const headingStyle = {
      fontSize: headingSize,
      fontWeight: 'bold',
      color: palette.headingColor,  // ✅ ONLY headings get selected color
      fontFamily: font,
      fontStyle: fontStyle,
      mb: `${paragraphSpacing / 10}px`,
      lineHeight: `${lineSpacing}px`
    };

    switch (sectionId) {
      case 'about':
        return (
          <Box className="resume-section" id="about-section" sx={sectionContainerStyle}>
            <SectionHeader color={palette.headingColor} sx={headingStyle}>
              ABOUT ME
            </SectionHeader>
            <Typography variant="body1" sx={paragraphStyle}>
              {savedSummary || data.profile}
            </Typography>
          </Box>
        );
      
      case 'skills':
        return (
          <Box className="resume-section" id="skills-section" sx={sectionContainerStyle}>
            <SectionHeader color={palette.headingColor} sx={headingStyle}>
              SKILLS
            </SectionHeader>
            <Box sx={{ mt: 2, fontFamily: font }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {savedSkills.length > 0 ? (
                  savedSkills.map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill.name}
                      variant="outlined"
                      sx={{
                        borderColor: palette.headingColor,  // ✅ Chip border gets color
                        color: palette.textColor,            // ✅ Chip text stays dark
                        fontFamily: font,
                        fontStyle: fontStyle,
                        fontSize: fontSize,
                        mb: `${paragraphSpacing / 20}px`
                      }}
                    />
                  ))
                ) : (
                  data.skills.map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill}
                      variant="outlined"
                      sx={{
                        borderColor: palette.headingColor,
                        color: palette.textColor,
                        fontFamily: font,
                        fontStyle: fontStyle,
                        fontSize: fontSize,
                        mb: `${paragraphSpacing / 20}px`
                      }}
                    />
                  ))
                )}
              </Box>
            </Box>
          </Box>
        );

      case 'languages':
        return savedLanguages.length > 0 ? (
          <Box className="resume-section" id="languages-section" sx={sectionContainerStyle}>
            <SectionHeader color={palette.headingColor} sx={headingStyle}>
              LANGUAGES
            </SectionHeader>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {savedLanguages.map((lang, index) => (
                  <Chip
                    key={index}
                    label={`${lang.name}${lang.level ? ` - ${lang.level}` : ''}`}
                    variant="outlined"
                    sx={{
                      borderColor: palette.headingColor,
                      color: palette.textColor,
                      fontFamily: font,
                      fontStyle: fontStyle,
                      fontSize: fontSize,
                      mb: `${paragraphSpacing / 20}px`
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <Box className="resume-section" id="certifications-section" sx={sectionContainerStyle}>
            <SectionHeader color={palette.headingColor} sx={headingStyle}>
              CERTIFICATIONS
            </SectionHeader>
            <Box sx={{ mt: 2 }}>
              {savedCertifications.map((cert, index) => (
                <Box key={index} sx={{ mb: `${paragraphSpacing / 10}px` }}>
                  <Typography variant="body2" sx={{ ...paragraphStyle, fontWeight: 'bold' }}>
                    {cert.name}
                  </Typography>
                  <Typography variant="body2" sx={paragraphStyle}>
                    {cert.provider} ({cert.year})
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'softwareSkills':
        return softwareSkills.length > 0 ? (
          <Box className="resume-section" id="software-skills-section" sx={sectionContainerStyle}>
            <SectionHeader color={palette.headingColor} sx={headingStyle}>
              SOFTWARE SKILLS
            </SectionHeader>
            <Box sx={{ mt: 2 }}>
              {softwareSkills.map((skill, index) => (
                <Box key={index} sx={{ mb: `${paragraphSpacing / 10}px` }}>
                  <Typography variant="body2" sx={paragraphStyle}>
                    {skill.name} — {skill.level}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <Box className="resume-section" id="interests-section" sx={sectionContainerStyle}>
            <SectionHeader color={palette.headingColor} sx={headingStyle}>
              INTERESTS
            </SectionHeader>
            <Box sx={{ mt: 2 }}>
              {savedInterests.map((interest, index) => (
                <Typography key={index} variant="body2" sx={paragraphStyle}>
                  • {interest}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <Box className="resume-section" id="accomplishments-section" sx={sectionContainerStyle}>
            <SectionHeader color={palette.headingColor} sx={headingStyle}>
              ACCOMPLISHMENTS
            </SectionHeader>
            <Box sx={{ mt: 2 }}>
              {savedAccomplishments.map((accomplishment, index) => (
                <Typography key={index} variant="body2" sx={{ ...paragraphStyle, whiteSpace: 'pre-line' }}>
                  • {accomplishment}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'experience':
        return (
          <Box className="resume-section" id="experience-section" sx={sectionContainerStyle}>
            <SectionHeader color={palette.headingColor} sx={headingStyle}>
              WORK EXPERIENCE
            </SectionHeader>

            {(workExperiences && workExperiences.length > 0) ? (
              workExperiences.map((work, index) => (
                <Box key={index} sx={{ mt: 3, mb: `${sectionSpacing / 10}px` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 'bold', 
                      color: palette.textColor,   // ✅ Job title stays dark
                      fontFamily: font,
                      fontStyle: fontStyle,
                      fontSize: `calc(${headingSize} * 0.9)`,
                      mb: `${paragraphSpacing / 20}px`
                    }}>
                      {work.jobTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: palette.lightText,   // ✅ Date stays gray
                      fontFamily: font,
                      fontStyle: fontStyle,
                      fontSize: fontSize,
                      mb: `${paragraphSpacing / 20}px`
                    }}>
                      {formatDate(work.startMonth, work.startYear)} - {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ 
                    color: palette.lightText,    // ✅ Employer stays gray
                    mb: `${paragraphSpacing / 10}px`, 
                    fontFamily: font, 
                    fontStyle: fontStyle,
                    fontSize: `calc(${fontSize} * 1.1)`
                  }}>
                    {work.employer}
                  </Typography>
                  {work.description && (
                    <Box
                      sx={{ 
                        mt: 1, 
                        color: palette.textColor,
                        fontFamily: font,
                        fontSize: fontSize,
                        fontStyle: fontStyle,
                        lineHeight: `${lineSpacing}px`
                      }}
                      dangerouslySetInnerHTML={{
                        __html: work.description
                          .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family: ${font}; font-weight: bold; color: ${palette.textColor}; font-size: ${fontSize}; line-height: ${lineSpacing}px">$1</strong>`)
                          .replace(/\*(.*?)\*/g, `<em style="font-family: ${font}; font-style: italic; color: ${palette.textColor}; font-size: ${fontSize}; line-height: ${lineSpacing}px">$1</em>`)
                          .replace(/<u>(.*?)<\/u>/g, `<u style="font-family: ${font}; color: ${palette.textColor}; font-size: ${fontSize}; line-height: ${lineSpacing}px">$1</u>`)
                          .replace(/\n/g, `<br />`),
                      }}
                    />
                  )}
                  {index < workExperiences.length - 1 && (
                    <Divider sx={{ 
                      my: `${sectionSpacing / 20}px`, 
                      borderColor: palette.borderColor,  // ✅ Divider fixed gray
                      borderBottomWidth: lineWeight 
                    }} />
                  )}
                </Box>
              ))
            ) : (
              <>
                <Box sx={{ mt: 3, mb: `${sectionSpacing / 10}px` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 'bold', color: palette.textColor, fontFamily: font, fontStyle: fontStyle,
                      fontSize: `calc(${headingSize} * 0.9)`, mb: `${paragraphSpacing / 20}px`
                    }}>
                      Consumer Goods Seller
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: palette.lightText, fontFamily: font, fontStyle: fontStyle,
                      fontSize: fontSize, mb: `${paragraphSpacing / 20}px`
                    }}>
                      Aug 2018 - Present
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ 
                    color: palette.lightText, mb: `${paragraphSpacing / 10}px`, 
                    fontFamily: font, fontStyle: fontStyle, fontSize: `calc(${fontSize} * 1.1)`
                  }}>
                    Impressive Industries
                  </Typography>
                  <List disablePadding>
                    <ListItem sx={{ px: 0, py: `${paragraphSpacing / 40}px` }}>
                      <BulletPoint color={palette.textColor}>•</BulletPoint>
                      <Typography variant="body2" sx={{ 
                        fontFamily: font, fontStyle: fontStyle, textIndent: `${paragraphIndent}px`, 
                        lineHeight: `${lineSpacing}px`, color: palette.textColor, fontSize: fontSize
                      }}>
                        Offer consumer goods packages to corporate and clients
                      </Typography>
                    </ListItem>
                    <ListItem sx={{ px: 0, py: `${paragraphSpacing / 40}px` }}>
                      <BulletPoint color={palette.textColor}>•</BulletPoint>
                      <Typography variant="body2" sx={{ 
                        fontFamily: font, fontStyle: fontStyle, textIndent: `${paragraphIndent}px`, 
                        lineHeight: `${lineSpacing}px`, color: palette.textColor, fontSize: fontSize
                      }}>
                        Meet with clients every quarter to update or renew services
                      </Typography>
                    </ListItem>
                  </List>
                </Box>
                <Divider sx={{ my: `${sectionSpacing / 20}px`, borderColor: palette.borderColor, borderBottomWidth: lineWeight }} />
                <Box sx={{ mb: `${sectionSpacing / 10}px` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 'bold', color: palette.textColor, fontFamily: font, fontStyle: fontStyle,
                      fontSize: `calc(${headingSize} * 0.9)`, mb: `${paragraphSpacing / 20}px`
                    }}>
                      PMCG Sales Agent
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: palette.lightText, fontFamily: font, fontStyle: fontStyle,
                      fontSize: fontSize, mb: `${paragraphSpacing / 20}px`
                    }}>
                      Jul 2016 - Aug 2018
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ 
                    color: palette.lightText, mb: `${paragraphSpacing / 10}px`, 
                    fontFamily: font, fontStyle: fontStyle, fontSize: `calc(${fontSize} * 1.1)`
                  }}>
                    Fast-moving Industries
                  </Typography>
                  <List disablePadding>
                    <ListItem sx={{ px: 0, py: `${paragraphSpacing / 40}px` }}>
                      <BulletPoint color={palette.textColor}>•</BulletPoint>
                      <Typography variant="body2" sx={{ 
                        fontFamily: font, fontStyle: fontStyle, textIndent: `${paragraphIndent}px`, 
                        lineHeight: `${lineSpacing}px`, color: palette.textColor, fontSize: fontSize
                      }}>
                        Visited corporate client offices to renew latest products
                      </Typography>
                    </ListItem>
                    <ListItem sx={{ px: 0, py: `${paragraphSpacing / 40}px` }}>
                      <BulletPoint color={palette.textColor}>•</BulletPoint>
                      <Typography variant="body2" sx={{ 
                        fontFamily: font, fontStyle: fontStyle, textIndent: `${paragraphIndent}px`, 
                        lineHeight: `${lineSpacing}px`, color: palette.textColor, fontSize: fontSize
                      }}>
                        Built relationships with clients to maintain sales goals and create new opportunities
                      </Typography>
                    </ListItem>
                  </List>
                </Box>
              </>
            )}
          </Box>
        );
      
      case 'education':
        return (
          <Box className="resume-section" id="education-section" sx={sectionContainerStyle}>
            <SectionHeader color={palette.headingColor} sx={headingStyle}>
              EDUCATION
            </SectionHeader>
            <Box sx={{ mt: 2 }}>
              {educationEntries.length > 0 ? (
                educationEntries.map((edu) => {
                  const degreeText = edu.educationLevel === "school" 
                    ? (edu.standard === "10th" ? "10th Standard" : edu.standard === "11th" ? "11th Standard" :
                       edu.standard === "12th" ? "12th Standard" : edu.standard === "Other" ? "Other" : edu.standard || "School")
                    : (edu.degreeName || edu.degreeType || "Degree");
                  
                  const fieldOfStudy = edu.educationLevel === "school" ? edu.stream : edu.specialization;
                  const schoolName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
                  const schoolLocation = edu.educationLevel === "school" ? edu.schoolLocation : edu.collegeLocation;
                  const gradMonth = edu.gradMonth || "";
                  const gradYear = edu.gradYear || "";
                  
                  return (
                    <Box key={edu.id} sx={{ mb: `${sectionSpacing / 10}px` }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 'bold', color: palette.textColor,  // ✅ Degree name stays dark
                          fontFamily: font, fontStyle: fontStyle,
                          fontSize: `calc(${headingSize} * 0.9)`, mb: `${paragraphSpacing / 20}px`
                        }}>
                          {degreeText} {fieldOfStudy ? `- ${fieldOfStudy}` : ''}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: palette.lightText,  // ✅ Date stays gray
                          fontFamily: font, fontStyle: fontStyle,
                          fontSize: fontSize, mb: `${paragraphSpacing / 20}px`
                        }}>
                          {gradMonth && gradYear ? formatDate(gradMonth, gradYear) : ''}
                        </Typography>
                      </Box>
                      <Typography variant="subtitle1" sx={{ 
                        color: palette.lightText,   // ✅ School name stays gray
                        fontFamily: font, fontStyle: fontStyle,
                        fontSize: `calc(${fontSize} * 1.1)`, mb: `${paragraphSpacing / 10}px`
                      }}>
                        {schoolName}{schoolLocation ? `, ${schoolLocation}` : ''}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {(edu.percentage || edu.cgpa || edu.grade) && (
                          <Typography variant="body2" sx={{ 
                            fontFamily: font, fontStyle: fontStyle, color: palette.textColor,
                            fontSize: fontSize, lineHeight: `${lineSpacing}px`, mb: 0.5
                          }}>
                            {edu.percentage && `Percentage: ${edu.percentage}%`}
                            {edu.cgpa && `${edu.percentage ? ' • ' : ''}CGPA: ${edu.cgpa}`}
                            {edu.grade && `${(edu.percentage || edu.cgpa) ? ' • ' : ''}Grade: ${edu.grade}`}
                          </Typography>
                        )}
                        {edu.educationLevel === "school" && edu.district && (
                          <Typography variant="body2" sx={{ 
                            fontFamily: font, fontStyle: fontStyle, color: palette.textColor,
                            fontSize: fontSize, lineHeight: `${lineSpacing}px`, mb: 0.5
                          }}>
                            District: {edu.district}
                          </Typography>
                        )}
                        {edu.educationLevel === "college" && edu.programType && (
                          <Typography variant="body2" sx={{ 
                            fontFamily: font, color: palette.lightText,
                            fontSize: fontSize, lineHeight: `${lineSpacing}px`, mb: 0.5, fontStyle: 'italic'
                          }}>
                            {edu.programType === "UG" ? "Undergraduate" : edu.programType === "PG" ? "Postgraduate" :
                             edu.programType === "Diploma" ? "Diploma" : edu.programType === "Certificate" ? "Certificate" : edu.programType}
                          </Typography>
                        )}
                        {edu.additionalCoursework && (
                          <Typography variant="body2" sx={{ 
                            fontFamily: font, fontStyle: fontStyle, color: palette.textColor,
                            fontSize: fontSize, lineHeight: `${lineSpacing}px`, mt: 1, p: 1,
                            backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1
                          }}>
                            Additional Coursework: {edu.additionalCoursework}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <Box sx={{ mb: `${sectionSpacing / 10}px` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 'bold', color: palette.textColor, fontFamily: font, fontStyle: fontStyle,
                      fontSize: `calc(${headingSize} * 0.9)`, mb: `${paragraphSpacing / 20}px`
                    }}>
                      B.A. Sales and Commerce
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: palette.lightText, fontFamily: font, fontStyle: fontStyle,
                      fontSize: fontSize, mb: `${paragraphSpacing / 20}px`
                    }}>
                      2019 - 2024
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ 
                    color: palette.lightText, fontFamily: font, fontStyle: fontStyle,
                    fontSize: `calc(${fontSize} * 1.1)`
                  }}>
                    Western University
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <Box className="resume-section" id="additional-info-section" sx={sectionContainerStyle}>
            <SectionHeader color={palette.headingColor} sx={headingStyle}>
              ADDITIONAL INFORMATION
            </SectionHeader>
            <Box sx={{ mt: 2 }}>
              {savedAdditionalInfo.map((info, index) => (
                <Typography key={index} variant="body2" sx={paragraphStyle}>• {info}</Typography>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <Box className="resume-section" id="volunteering-section" sx={sectionContainerStyle}>
            <SectionHeader color={palette.headingColor} sx={headingStyle}>
              VOLUNTEERING
            </SectionHeader>
            <Box sx={{ mt: 2 }}>
              {savedVolunteering.map((vol, index) => (
                <Box key={index} sx={{ mb: `${sectionSpacing / 10}px` }}>
                  <Typography variant="body2" sx={{ ...paragraphStyle, fontWeight: 'bold' }}>
                    {vol.subtopic}
                  </Typography>
                  <Typography variant="body2" sx={{ ...paragraphStyle, color: palette.lightText, fontStyle: 'italic' }}>
                    {vol.fromDate} - {vol.toDate}
                  </Typography>
                  {vol.content && (
                    <Typography variant="body2" sx={{ ...paragraphStyle, whiteSpace: 'pre-line' }}>
                      {vol.content}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <Box className="resume-section" id="references-section" sx={sectionContainerStyle}>
            <SectionHeader color={palette.headingColor} sx={headingStyle}>
              REFERENCES
            </SectionHeader>
            <Box sx={{ mt: 2 }}>
              {savedReferences.map((ref, index) => (
                <Typography key={index} variant="body2" sx={{ ...paragraphStyle, whiteSpace: 'pre-line' }}>
                  {ref}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'websites':
        return savedWebsites.length > 0 ? (
          <Box className="resume-section" id="websites-section" sx={sectionContainerStyle}>
            <SectionHeader color={palette.headingColor} sx={headingStyle}>
              WEBSITES / PROFILES
            </SectionHeader>
            <Box sx={{ mt: 2 }}>
              {savedWebsites.map((site, index) => (
                <Typography key={index} variant="body2" sx={paragraphStyle}>
                  • <a 
                      href={site.url.startsWith('http') ? site.url : `https://${site.url}`}
                      style={{ color: palette.headingColor, fontFamily: font, fontStyle: fontStyle, fontSize: fontSize, textDecoration: 'none' }} 
                      target="_blank" rel="noopener noreferrer"
                    >
                      {site.name || site.url}
                    </a>
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          const validItems = items.filter(item => item && item.trim() !== '');
          if (validItems.length > 0) {
            return (
              <Box className="resume-section" id={`custom-${customSectionName}-section`} sx={sectionContainerStyle}>
                <SectionHeader color={palette.headingColor} sx={headingStyle}>
                  {customSectionName.replace(/_/g, ' ').toUpperCase()}
                </SectionHeader>
                <Box sx={{ mt: 2 }}>
                  {validItems.map((item, i) => (
                    <Box key={i} sx={{ mb: `${paragraphSpacing / 10}px` }}>
                      <Typography sx={paragraphStyle} whiteSpace="pre-line">• {item}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          }
          return null;
        }
        return null;
    }
  };

  const splitContentIntoPages = useCallback(() => {
    if (!resumeRef.current) return;
    
    const sections = Array.from(resumeRef.current.querySelectorAll('.resume-section'));
    const headerHeight = resumeRef.current.querySelector('.resume-header')?.offsetHeight || 0;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const availableHeightFirstPage = pageHeightInPx - headerHeight - (2 * topBottomMargin) - 2 * 16;
    const availableHeightSubsequent = pageHeightInPx - (2 * topBottomMargin) - 2 * 16;

    let newPages = [];
    let currentPageSections = [];
    let currentHeight = 0;

    sections.forEach(section => {
      const sectionHeight = section.offsetHeight;
      const pageLimit = newPages.length === 0 ? availableHeightFirstPage : availableHeightSubsequent;
      if (currentHeight + sectionHeight > pageLimit) {
        newPages.push(currentPageSections);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
    });

    if (currentPageSections.length > 0) newPages.push(currentPageSections);
    setPages(newPages);
  }, [page.height, topBottomMargin, workExperiences, educationEntries, savedSkills, savedSummary, savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications, savedReferences, softwareSkills, savedVolunteering, savedInterests, savedWebsites, customSections, sectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color]);
  
  useEffect(() => {
    const timeoutId = setTimeout(() => splitContentIntoPages(), 500);
    return () => clearTimeout(timeoutId);
  }, [splitContentIntoPages]);

  const saveDownloadHistory = async (format, fileName, fileSize) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      await fetch("https://resume-maker-lc.onrender.com/api/resumes/save-download", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ templateId: 18, fileName, fileFormat: format, fileSize, themeColor: color, downloadedAt: new Date().toISOString() }),
      });
    } catch (error) {
      console.error("Error saving download history:", error);
    }
  };

  const handlePDFDownload = async () => {
    const resumeElement = document.getElementById('resume-container');
    if (!resumeElement) return;
    try {
      const resumePages = resumeElement.querySelectorAll('.resume-page');
      if (resumePages.length === 0) return;
      const pdf = new jsPDF("p", "mm", pageSize.toLowerCase());
      const pageWidth = pdf.internal.pageSize.getWidth();
      for (let i = 0; i < resumePages.length; i++) {
        if (i > 0) pdf.addPage();
        const canvas = await html2canvas(resumePages[i], { scale: 2, useCORS: true, allowTaint: true, backgroundColor: "#ffffff", logging: false });
        const imgData = canvas.toDataURL("image/png");
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight, undefined, 'FAST');
      }
      const pdfBlob = pdf.output('blob');
      await saveDownloadHistory("pdf", "resume", pdfBlob.size);
      pdf.save("resume.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
  };

  const handlePNGDownload = async () => {
    const resumeElement = document.getElementById('resume-container');
    if (!resumeElement) return;
    try {
      const resumePages = resumeElement.querySelectorAll('.resume-page');
      if (resumePages.length === 0) return;
      for (let i = 0; i < resumePages.length; i++) {
        const canvas = await html2canvas(resumePages[i], { scale: 2, useCORS: true, allowTaint: true, backgroundColor: "#ffffff" });
        const link = document.createElement('a');
        link.download = resumePages.length === 1 ? `resume.png` : `resume-page-${i + 1}.png`;
        link.href = canvas.toDataURL();
        if (i === 0) link.click();
        else setTimeout(() => link.click(), i * 500);
      }
      await saveDownloadHistory("png", "resume", 0);
      if (resumePages.length > 1) alert(`Downloading ${resumePages.length} PNG files.`);
    } catch (error) {
      console.error("Error generating PNG:", error);
      alert("Error generating PNG files. Please try again.");
    }
  };

  const handlePrint = () => window.print();
  const handleDownload = (format) => format === 'pdf' ? handlePDFDownload() : handlePNGDownload();

  // ✅ FIX: addFontStylesToHTML - simplify to let children inherit and keep React inline styles
  const addFontStylesToHTML = (htmlContent) => {
    return htmlContent;
  };

  const renderContentForMeasurement = () => (
    <Box ref={resumeRef} sx={{ visibility: 'hidden', position: 'absolute', fontFamily: font }}>
      <Container sx={{ 
        width: pageSize === "A4" ? "210mm" : "216mm",
        minHeight: pageSize === "A4" ? "297mm" : "279mm",
        p: sideMargins / 5, 
        backgroundColor: palette.header, 
        boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
        border: `${lineWeight}px solid ${palette.borderColor}`,  // ✅ Border fixed gray
        my: topBottomMargin / 10,
        fontFamily: font, fontSize: fontSize, fontStyle: fontStyle,
        lineHeight: `${lineSpacing}px`, color: palette.textColor,
      }}>
        {/* Header Section */}
        <Box className="resume-section resume-header" sx={{ 
          p: 4, mb: 4, 
          borderBottom: `${lineWeight}px solid ${palette.borderColor}`,  // ✅ Header border fixed gray
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          backgroundColor: palette.sidebarBackground,
          fontFamily: font, color: palette.textColor,
        }}>
          <Box sx={{ fontFamily: font }}>
            <Typography variant="h2" sx={{ 
              fontWeight: 'bold', color: palette.nameColor,  // ✅ Name uses nameColor (separate control)
              fontSize: `calc(${headingSize} * 1.8)`,
              fontFamily: font, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`,
              mb: `${paragraphSpacing / 10}px`
            }}>
              {data.firstName} {data.lastName}
            </Typography>
            <Typography variant="h5" sx={{ 
              color: palette.lightText,  // ✅ Position stays gray
              mt: 1, fontSize: `calc(${headingSize} * 0.8)`,
              fontFamily: font, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`,
              mb: `${paragraphSpacing / 10}px`
            }}>
              {data.currentPosition}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', fontFamily: font }}>
            {data.email && (
              <ContactInfo color={palette.iconColor}>
                <EmailIcon sx={{ color: palette.iconColor }} />
                <Typography variant="body2" sx={{ color: palette.textColor, fontFamily: font, fontStyle: fontStyle, fontSize: fontSize, lineHeight: `${lineSpacing}px`, mb: `${paragraphSpacing / 20}px` }}>
                  {data.email}
                </Typography>
              </ContactInfo>
            )}
            {data.phone && (
              <ContactInfo color={palette.iconColor}>
                <PhoneIcon sx={{ color: palette.iconColor }} />
                <Typography variant="body2" sx={{ color: palette.textColor, fontFamily: font, fontStyle: fontStyle, fontSize: fontSize, lineHeight: `${lineSpacing}px`, mb: `${paragraphSpacing / 20}px` }}>
                  {data.phone}
                </Typography>
              </ContactInfo>
            )}
            {data.city && (
              <ContactInfo color={palette.iconColor}>
                <LocationOnIcon sx={{ color: palette.iconColor }} />
                <Typography variant="body2" sx={{ color: palette.textColor, fontFamily: font, fontStyle: fontStyle, fontSize: fontSize, lineHeight: `${lineSpacing}px`, mb: `${paragraphSpacing / 20}px` }}>
                  {data.city}
                </Typography>
              </ContactInfo>
            )}
            {data.linkedIn && (
              <ContactInfo color={palette.iconColor}>
                <LinkedInIcon sx={{ color: '#0077b5' }} />
                <Typography variant="body2" sx={{ color: palette.textColor, fontFamily: font, fontStyle: fontStyle, fontSize: fontSize, lineHeight: `${lineSpacing}px`, mb: `${paragraphSpacing / 20}px` }}>
                  <a 
                    href={data.linkedIn.startsWith('http') ? data.linkedIn : `https://${data.linkedIn}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: palette.textColor, textDecoration: 'none', fontFamily: font, fontSize: fontSize, fontStyle: fontStyle }}
                  >
                    {data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
                  </a>
                </Typography>
              </ContactInfo>
            )}
          </Box>
        </Box>

        {/* Main Content Grid */}
        <Grid container spacing={4} sx={{ fontFamily: font, color: palette.textColor }}>
          <Grid item xs={12} md={4} sx={{ fontFamily: font }}>
            {sectionOrder
              .filter(sectionId => {
                const leftColumnSections = ['about', 'skills', 'languages', 'certifications', 'softwareSkills', 'interests', 'accomplishments'];
                return leftColumnSections.includes(sectionId) || sectionId.startsWith('custom_');
              })
              .map(sectionId => {
                const sectionContent = renderSectionContent(sectionId);
                return sectionContent ? <React.Fragment key={sectionId}>{sectionContent}</React.Fragment> : null;
              })}
          </Grid>
          <Grid item xs={12} md={8} sx={{ fontFamily: font }}>
            {sectionOrder
              .filter(sectionId => {
                const rightColumnSections = ['experience', 'education', 'additionalInfo', 'volunteering', 'references', 'websites'];
                return rightColumnSections.includes(sectionId);
              })
              .map(sectionId => {
                const sectionContent = renderSectionContent(sectionId);
                return sectionContent ? <React.Fragment key={sectionId}>{sectionContent}</React.Fragment> : null;
              })}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", position: "relative", fontFamily: font, color: palette.textColor }}>
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, '@media print': { display: 'none' }, fontFamily: font }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2, fontFamily: font, color: palette.textColor }}>
            Page {currentPage + 1} of {Math.max(1, pages.length)}
          </Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(pages.length - 1, prev + 1))} disabled={currentPage === Math.max(0, pages.length - 1)} color="primary">
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 2 }}>
              <Button onClick={handleArrangeSectionsClick} variant="contained" startIcon={<Reorder />} color="primary">
                Arrange Sections
              </Button>
              <Button onClick={resetSectionOrder} variant="outlined" color="secondary" size="small">
                Reset Section Order
              </Button>
            </Box>
          )}
        </Box>
      )}
      
      {renderContentForMeasurement()}
      
      <Box id="resume-container" sx={{ fontFamily: font, color: palette.textColor }}>
        {pages.length > 0 ? (
          pages.map((pageContent, index) => (
            <Box
              key={index}
              className="resume-page"
              sx={{
                width: page.width,
                minHeight: page.height,
                mx: "auto",
                boxShadow: index === currentPage ? 3 : 0,
                bgcolor: "#fff",
                color: palette.textColor,
                fontFamily: font, fontSize, fontStyle,
                lineHeight: `${lineSpacing}px`,
                p: `${topBottomMargin}px ${sideMargins}px`,
                display: "flex", flexDirection: "column", pageBreakAfter: "always",
                border: `${lineWeight}px solid ${palette.borderColor}`,  // ✅ Border fixed gray
                "@media print": {
                  width: page.width, height: "auto", minHeight: page.height,
                  boxShadow: "none", m: 0, fontFamily: font, fontSize: fontSize,
                  fontStyle: fontStyle, color: palette.textColor,
                  border: `${lineWeight}px solid ${palette.borderColor}`,
                },
                display: exporting ? 'block' : (index === currentPage ? 'block' : 'none'),
                '@media print': { display: 'block' }
              }}
            >
              <div 
                style={{ fontFamily: font, fontStyle: fontStyle, fontSize: fontSize, lineHeight: `${lineSpacing}px`, color: palette.textColor }}
                dangerouslySetInnerHTML={{ __html: addFontStylesToHTML(pageContent.join('')) }} 
              />
            </Box>
          ))
        ) : (
          <Container sx={{ 
            width: pageSize === "A4" ? "210mm" : "216mm",
            minHeight: pageSize === "A4" ? "297mm" : "279mm",
            p: sideMargins / 5, backgroundColor: palette.header, 
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
            border: `${lineWeight}px solid ${palette.borderColor}`,
            my: topBottomMargin / 10, fontFamily: font, fontSize: fontSize,
            fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, color: palette.textColor,
          }}>
            <Typography sx={{ fontFamily: font, color: palette.textColor }}>Loading resume content...</Typography>
          </Container>
        )}
      </Box>

      <Dialog open={arrangeDialogOpen} onClose={handleArrangeDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag and drop to reorder sections.
          </Typography>
          {renderDragPanel()}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetSectionOrder} color="secondary">Reset Order</Button>
          <Button onClick={handleArrangeDialogClose} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const resumeMeta = {
  hasPhoto: false,
  columns: 2,
  colorOptions: [
    { value: '#2c3e50', label: 'Dark Blue' },
    { value: '#3498db', label: 'Blue' },
    { value: '#e74c3c', label: 'Red' },
    { value: '#2ecc71', label: 'Green' },
    { value: '#9b59b6', label: 'Purple' },
    { value: '#f39c12', label: 'Orange' }
  ],
  nameColorOptions: [
    { value: '#2c3e50', label: 'Dark Blue' },
    { value: '#34495e', label: 'Dark Gray' },
    { value: '#000000', label: 'Black' },
    { value: '#c0392b', label: 'Dark Red' }
  ],
  sidebarBackgroundOptions: [
    { value: '#f8f9fa', label: 'Light Gray' },
    { value: '#e6f3f9ff', label: 'Light Blue' },
    { value: '#f0f8e6ff', label: 'Light Green' },
    { value: '#f9e6f3ff', label: 'Light Pink' },
    { value: '#e6e6f9ff', label: 'Light Lavender' },
    { value: '#f9f3e6ff', label: 'Light Beige' }
  ],
  headerBackgroundOptions: [
    { value: '#fff', label: 'White' },
    { value: '#f8f9fa', label: 'Light Gray' },
    { value: '#e3f2fd', label: 'Very Light Blue' },
    { value: '#f3e5f5', label: 'Very Light Purple' },
    { value: '#e8f5e9', label: 'Very Light Green' }
  ],
  experienceLevel: ["No Experience", "Less than 3 years", "3-5 Years"]
};

export default Resume18;